import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import { SiteTemplatePreview } from "./SiteTemplatePreview";
import { StandSitePreviewModal } from "./StandSitePreviewModal";
import "./site-template-catalog.css";
import "./site-template-catalog-visual.css";
type Version = {
  id: string;
  version_number: number;
  config: {
    layout?: string;
    colors?: {
      accent?: string;
      background?: string;
      surface?: string;
      text?: string;
    };
  };
  status: string;
  change_note: string;
  created_at: string;
};
type Template = {
  id: string;
  slug: string;
  name: string;
  description: string;
  active: boolean;
  versions: Version[];
};
export function SiteTemplateCatalog() {
  const [items, setItems] = useState<Template[]>([]),
    [selected, setSelected] = useState<Template | null>(null),
    [previewing, setPreviewing] = useState<Template | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [saving, setSaving] = useState(false);
  const load = async () => {
    const { data, error } = await supabase.rpc("get_site_template_catalog");
    if (error) setNotice(error.message);
    else setItems((data ?? []) as Template[]);
  };
  useEffect(() => {
    void load();
  }, []);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget),
      current = selected?.versions[0]?.config ?? {};
    const config = {
      ...current,
      layout: String(form.get("layout")),
      colors: {
        accent: String(form.get("accent")),
        background: String(form.get("background")),
        surface: String(form.get("surface")),
        text: String(form.get("text")),
      },
      typography: { heading: "system", body: "system" },
      blocks: ["header", "hero", "stock", "about", "contact", "footer"],
    };
    const { data: id, error } = await supabase.rpc(
      "save_site_template_version",
      {
        p_template_id: selected?.id || null,
        p_name: form.get("name"),
        p_description: form.get("description"),
        p_config: config,
        p_note: form.get("note"),
      },
    );
    if (error) {
      setSaving(false);
      setNotice(error.message);
      return;
    }
    const publish = await supabase.rpc("publish_site_template_version", {
      p_version_id: id,
    });
    setSaving(false);
    setNotice(publish.error?.message ?? "Template publicado.");
    if (!publish.error) {
      setSelected(null);
      await load();
    }
  };
  return (
    <section className="template-catalog">
      <header>
        <div>
          <p className="eyebrow">Sites dos stands</p>
          <p>
            Crie e publique estruturas visuais seguras para os stands
            personalizarem.
          </p>
        </div>
        <button
          className="primary-button compact"
          onClick={() =>
            setSelected({
              id: "",
              slug: "",
              name: "",
              description: "",
              active: true,
              versions: [],
            })
          }
        >
          Novo template
        </button>
      </header>
      {notice && (
        <button
          className="notice"
          role="status"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="template-grid">
        {items.map((item) => {
          const published=item.versions.find((version)=>version.status==="published")??item.versions[0];
          return <article key={item.id}>
            <SiteTemplatePreview config={published?.config??{}} name={item.name}/>
            <div className="template-card-copy"><strong>{item.name}</strong><p>{item.description}</p><small>{published?`Versão ${published.version_number} publicada`:"Sem versão publicada"}</small></div>
            <footer><button type="button" onClick={()=>setPreviewing(item)}>Pré-visualizar</button><button type="button" className="primary-button" onClick={()=>setSelected(item)}>Editar template</button></footer>
          </article>;
        })}
      </div>
      {selected && (
        <div className="modal-backdrop">
          <section className="template-dialog">
            <header>
              <div>
                <p className="eyebrow">Template do site</p>
                <h2>{selected.id ? selected.name : "Novo template"}</h2>
              </div>
              <button onClick={() => setSelected(null)}>×</button>
            </header>
            <form onSubmit={save}>
              {(() => {
                const colors =
                  selected.versions.find((v) => v.status === "published")
                    ?.config.colors ?? {};
                return (
                  <div className="template-fields">
                    <label>
                      <span>Nome</span>
                      <small className="field-help">Nome interno apresentado aos stands no seletor de templates.</small>
                      <input
                        name="name"
                        required
                        defaultValue={selected.name}
                      />
                    </label>
                    <label>
                      <span>Estrutura</span>
                      <small className="field-help">Composição visual que define a posição do cabeçalho, abertura, stock e rodapé.</small>
                      <select
                        name="layout"
                        defaultValue={
                          selected.versions[0]?.config.layout ?? "avenida"
                        }
                      >
                        <option value="avenida">Avenida</option>
                        <option value="atelier">Atelier</option>
                        <option value="circuit">Circuit</option>
                        <option value="horizonte">Horizonte</option>
                        <option value="galeria">Galeria</option>
                        <option value="mercado">Mercado</option>
                      </select>
                    </label>
                    <label className="wide">
                      <span>Descrição</span>
                      <small className="field-help">Explicação curta para ajudar o stand a escolher este template.</small>
                      <textarea
                        name="description"
                        required
                        rows={3}
                        defaultValue={selected.description}
                      />
                    </label>
                    <label>
                      <span>Cor principal</span>
                      <small className="field-help">Usada em botões, links e destaques.</small>
                      <input
                        name="accent"
                        type="color"
                        defaultValue={colors.accent ?? "#49f5c1"}
                      />
                    </label>
                    <label>
                      <span>Fundo</span>
                      <small className="field-help">Cor base das páginas.</small>
                      <input
                        name="background"
                        type="color"
                        defaultValue={colors.background ?? "#061712"}
                      />
                    </label>
                    <label>
                      <span>Blocos</span>
                      <small className="field-help">Cor dos cartões e áreas destacadas sobre o fundo.</small>
                      <input
                        name="surface"
                        type="color"
                        defaultValue={colors.surface ?? "#0d241d"}
                      />
                    </label>
                    <label>
                      <span>Texto</span>
                      <small className="field-help">Cor principal da leitura; deve manter contraste com o fundo.</small>
                      <input
                        name="text"
                        type="color"
                        defaultValue={colors.text ?? "#f5fff9"}
                      />
                    </label>
                    <label className="wide">
                      <span>Nota da versão</span>
                      <small className="field-help">Registo interno do que mudou; será preservado no histórico do template.</small>
                      <input
                        name="note"
                        required
                        placeholder="O que mudou nesta versão?"
                      />
                    </label>
                  </div>
                );
              })()}
              <footer>
                <button type="button" onClick={() => setSelected(null)}>
                  Cancelar
                </button>
                <button className="primary-button" disabled={saving}>
                  {saving ? "A guardar…" : "Guardar"}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {previewing&&(()=>{const published=previewing.versions.find((version)=>version.status==="published")??previewing.versions[0];return <StandSitePreviewModal demo template={{name:previewing.name,config:published?.config??{}}} standName="Stand de demonstração" content={{identity:{name:"Stand de demonstração",tagline:"Confiança em cada quilómetro"},hero:{headline:"Encontre a próxima viatura certa para si",intro:"Stock selecionado, acompanhamento próximo e resposta rápida da nossa equipa.",button:"Ver viaturas"},colors:{accent:published?.config.colors?.accent??"#49f5c1"},pages:[]}} onClose={()=>setPreviewing(null)}/>;})()}
    </section>
  );
}
